import { DataModule } from "./DataModule.js";
import * as dtt from "../../data/model/data-types.js";
import * as dto from "../../data/model/data-objects.js";
import * as browserHelpers from "../utils/data-browser-helpers.js";
import * as editorHelpers from "../utils/data-editor-helpers.js";
import { Services } from "../../services/Services.js";
import { DataQueryManager } from "../../data/queries/DataQueryManager.js";
import { DataTable } from "../controls/data/DataTable.js";
import { DataEditorControl } from "../controls/data/DataEditorControl.js";
import { ModalDialog } from "../controls/dialogs/ModalDialog.js";
import { Button } from "../controls/Button.js";
import { Text } from "../controls/Text.js";
import { resolveValue } from "../utils/ui-helpers.js";

export class DataBrowser extends DataModule {

    static MODE_READ = 'read';
    static MODE_UPDATE = 'update';

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: () => this.getDataModel().getEntityName() + ':Browser',
            moduleTitle: () => this.getDataModel().getSetLabel(),
        }, options);

        super(parent, options);

        const {
            defaultDataQueryString = () => {
                const config = Services.get('config');
                return `$limit=${config.paginator.defaultPageSize}`;
            },
            browserRouteName = null,
            editorRouteName = null,
        } = options;

        this.columnDefs = options.columnDefs ?? null;
        this.browserFields = options.browserFields ?? null;
        this.editorDefs = options.editorDefs ?? null;
        this.editorFields = options.editorFields ?? null;
        this.resolvedColumnDefs = null;
        this.resolvedEditorDefs = null;

        this.dataFilterMode = DataBrowser.MODE_READ;
        this.viewMode = DataBrowser.MODE_READ;

        this.dataset = {
            count: 0,
            limit: null,
            offset: 0,
            data: [],
        };
        this.columns = [];

        this.defaultDataQueryString = defaultDataQueryString;
        this.browserRouteName = browserRouteName;
        this.editorRouteName = editorRouteName;
        this.queryManager = DataQueryManager.create(this, {
            defaultQueryString: () => this.getDefaultDataQueryString(),
            fields: () => {
                const model = this.getDataModel();
                return (model && model.getFields()) || [];
            },
            onQueryChange: () => {
                this.applyFilter();
            },
        });
        this.queryManager.setQueryString(this.getDefaultDataQueryString());

        this.activeItem = null;
        this.activeItemBackup = null;
        this.activeItemEditor = null;
        this.editDialog = null;

        this.uiDataTable = new DataTable(this, {
            data: () => this.getData(),
            dataModel: () => this.getDataModel(),
            queryManager: () => this.getQueryManager(),
            columns: () => this.getColumns(),
            userCanReadData: () => this.canUserReadData(),
            userCanUpdateData: () => this.canUserUpdateData(),
            userCanDeleteData: () => this.canUserDeleteData(),
            onSetAllItemsSelection: (value) => this.setAllItemsSelection(value),
            onSetAllItemsDetails: (value) => this.setAllItemsDetails(value),
            onToggleItemSelection: (item) => this.toggleItemSelection(item),
            onToggleItemDetails: (item) => this.toggleItemDetails(item),
            onOpenItem: (item) => this.openItem(item),
            onEditItem: (item) => this.editItem(item),
            onDeleteItem: (item) => this.deleteItem(item),
        });

    };

    // --------------------------------------------------------------------------

    getDataFilterMode() {

        return this.dataFilterMode;

    };

    getViewMode() {

        return this.viewMode;

    };

    setViewMode(mode) {

        this.viewMode = mode || DataBrowser.MODE_READ;
        return this.viewMode;

    };

    changeViewMode(mode, invalidate = true) {

        this.setViewMode(mode);
        if (invalidate)
            this.invalidate();
        return this.viewMode;

    };

    toggleDataFilterMode() {

        this.dataFilterMode = (this.dataFilterMode == DataBrowser.MODE_READ ? DataBrowser.MODE_UPDATE : DataBrowser.MODE_READ);
        this.invalidate();

    };

    // --------------------------------------------------------------------------

    getDataset() {

        return (typeof this.dataset == 'function') ? this.dataset.call(this) : this.dataset;

    };

    setDataset(dataset) {

        this.dataset = dataset || {
            count: 0,
            limit: null,
            offset: 0,
            data: [],
        };

        return this.dataset;

    };

    setData(data) {

        const dataset = this.getDataset() || {};
        dataset.data = data || [];
        this.setDataset(dataset);

    };

    getData() {

        let ds = this.getDataset();
        return (ds && ds.data) || [];

    };

    getSelectedItems() {

        return (this.getData() || []).filter(function (item) { return item.$selected; });

    };

    getActiveItem() {

        return this.activeItem;

    };

    setActiveItem(item, suppressRefresh) {

        this.activeItem = item;
        if (!suppressRefresh) {
            this.refresh();
        }

    };

    // --------------------------------------------------------------------------

    getQueryManager() { return this.queryManager; }

    getDefaultDataQueryString() { return resolveValue(this.defaultDataQueryString); }

    getDataQueryString() {

        var qm = this.getQueryManager();
        return qm.getDataQueryString();
        //return (query && query.compile()) || null;

    };

    prepareDataQuery() {

        const defaults = this.getDefaultDataQueryString();
        this.getQueryManager().setQueryString(defaults);

    };

    getModuleState() {

        return {
            queryString: this.getDataQueryString(),
            viewMode: this.getViewMode(),
            dataFilterMode: this.getDataFilterMode(),
        };

    };

    setModuleState(state) {

        if (!state)
            return;

        if (state.queryString) {
            this.getQueryManager().setQueryString(state.queryString);
        }

        if (state.viewMode) {
            this.setViewMode(state.viewMode);
        }

        if (state.dataFilterMode) {
            this.dataFilterMode = state.dataFilterMode;
        }

    };

    // --------------------------------------------------------------------------

    getEditorRouteName() {

        return this.editorRouteName
            || this.getDataModel()?.editor?.moduleName
            || null;

    };

    getBrowserRouteName() {

        return this.browserRouteName
            || this.getDataModel()?.browser?.moduleName
            || null;

    };

    getItemRoute(id) {

        return this.getApp().getRouteHash(this.getEditorRouteName(), { id: id });

    };

    getItemUrl(id) {

        return this.getApp().getRouteUrl(this.getEditorRouteName(), { id: id });

    };

    resolveCreatedDataId(createdData) {

        const id = (createdData && typeof createdData == 'object')
            ? createdData.id
            : createdData;

        if (id === undefined || id === null || id === '')
            throw new Error('[DataBrowser] created data id is missing.');

        return id;

    };

    getApiUrl() {

        var service = this.getDataModel();
        return (service ? service.getDataServicePath() : '');

    };

    getExportUrl(format, ids) {

        let queryManager = this.getQueryManager(),
            queryString = queryManager.getDataQueryString({ compileLimit: false, compileOffset: false }) || '';
        return this.getApiUrl() + '?' + (queryString || '') + '&format=' + (format || 'csv') + (ids ? '&ids=' + ids : '');

    };

    // --------------------------------------------------------------------------

    getBrowsableFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.inBrowser);

    };

    getFilterableFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.filterable);

    };

    getPreviewFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.editable && !dtt.isCodelistType(field.datatype));

    };

    getNewRecordFields() {

        var fields = this.getDataModel().getFields() || [];
        return fields.filter((field) => field.required && !field.readOnly && (field.editable || field.inEditor));

    };

    getActiveItemEditor() {

        return this.activeItemEditor || null;

    };

    createActiveItemEditor(item) {

        return new DataEditorControl(null, {
            data: () => item,
            dataModel: () => this.getDataModel(),
            editorDefs: () => this.getEditorDefs(),
            readOnly: false,
            canUserEditField: () => true,
            onFieldValueChange: (value, field, actualData) => {
                field.setValue(actualData, value);
                actualData.$changed = true;
                this.invalidate();
                return true;
            }
        });

    };

    // --------------------------------------------------------------------------

    getColumns() {

        return this.columns;

    };

    getColumnsDefs() {

        return this.ensureColumnDefs();

    };

    getConfiguredColumnDefs() {

        return typeof this.columnDefs == 'function'
            ? this.columnDefs.call(this)
            : this.columnDefs;

    };

    getConfiguredBrowserFields() {

        return typeof this.browserFields == 'function'
            ? this.browserFields.call(this)
            : this.browserFields;

    };

    resolveColumnDefs() {

        const configuredColumnDefs = this.getConfiguredColumnDefs();
        if (configuredColumnDefs !== null && configuredColumnDefs !== undefined)
            return configuredColumnDefs;

        const configuredBrowserFields = this.getConfiguredBrowserFields();
        if (configuredBrowserFields !== null && configuredBrowserFields !== undefined)
            return browserHelpers.createColumnDefsFromFieldNames(this.getDataModel(), configuredBrowserFields);

        return browserHelpers.createColumnDefsFromModel(this.getDataModel());

    };

    ensureColumnDefs(force = false) {

        if (!force && this.resolvedColumnDefs !== null)
            return this.resolvedColumnDefs;

        this.resolvedColumnDefs = this.resolveColumnDefs() || [];
        return this.resolvedColumnDefs;

    };

    getConfiguredEditorDefs() {

        return typeof this.editorDefs == 'function'
            ? this.editorDefs.call(this)
            : this.editorDefs;

    };

    getConfiguredEditorFields() {

        return typeof this.editorFields == 'function'
            ? this.editorFields.call(this)
            : this.editorFields;

    };

    resolveEditorDefs() {

        const configuredEditorDefs = this.getConfiguredEditorDefs();
        if (configuredEditorDefs !== null && configuredEditorDefs !== undefined)
            return configuredEditorDefs;

        const configuredEditorFields = this.getConfiguredEditorFields();
        if (configuredEditorFields !== null && configuredEditorFields !== undefined)
            return editorHelpers.createEditorDefsFromFieldNames(this.getDataModel(), configuredEditorFields);

        return editorHelpers.createEditorDefsFromModel(this.getDataModel());

    };

    ensureEditorDefs(force = false) {

        if (!force && this.resolvedEditorDefs !== null)
            return this.resolvedEditorDefs;

        this.resolvedEditorDefs = this.resolveEditorDefs() || [];
        return this.resolvedEditorDefs;

    };

    getEditorDefs() {

        return this.ensureEditorDefs();

    };

    prepareColumns() {

        var columnsDefs = this.getColumnsDefs() || [],
            queryManager = this.getQueryManager(),
            orderBy = (queryManager && queryManager.getQueryOrder()) || [];

        if (columnsDefs.length) {
            this.columns = browserHelpers.createColumns(columnsDefs, orderBy);
            return this.columns;
        }

        this.columns = browserHelpers.createColumnsFromModel(this.getDataModel(), orderBy);
        return this.columns;

    };

    // --------------------------------------------------------------------------

    isReloadButtonEnabled() {
        return this.canUserReadData();
    };

    isEditButtonEnabled() {
        return this.getViewMode() == DataBrowser.MODE_READ && this.canUserReadData();
    };

    isApplyButtonEnabled() {
        return false;
    };

    isCancelButtonEnabled() {
        return false;
    };

    isDeleteButtonEnabled() {
        var selectedItems = this.getSelectedItems();
        return selectedItems && selectedItems.length && this.getViewMode() == DataBrowser.MODE_READ && this.canUserDeleteData();
    };

    isCreateButtonEnabled() {
        return this.getViewMode() == DataBrowser.MODE_READ && this.canUserCreateData();
    };

    isCloseButtonEnabled() {
        return true;
    };

    // --------------------------------------------------------------------------

    applyFilter() {

        this.storeModuleState();
        this.load();

    };

    applyQueryString(queryString) {

        let dataQuery = this.getQueryManager();
        dataQuery.setQueryString(queryString);
        this.applyFilter();

    };

    edit() {

        this.editItem(this.getActiveItem());

    };

    create() {

        var modalDialog = this.getNewRecordEditorControls();

        modalDialog.show(this.getApp());

    };

    delete() {

        this.deleteItem(this.getActiveItem());

    };

    close() {
        this.getApp().navigateBack();
    };

    // --------------------------------------------------------------------------

    getNewRecordEditorControls() {

        const i18n = Services.get('i18n');

        var that = this,
            model = this.getDataModel(),
            fields = this.getNewRecordFields(),
            newRecord = (fields || []).reduce(function (rec, field) { rec[field.name] = null; return rec; }, {}),
            modal = new ModalDialog(null, {
                title: model.getSetLabel() + ' – ' + i18n.t('module.data_browser.title_new_record'),
                controls: [],
                buttons: [
                    new Button(null, {
                        label: i18n.t('button.cancel.label'),
                        icon: 'cancel',
                        css: '.outlined',
                        onClick: onNewRecordCanceled,
                    }),
                    new Button(null, {
                        label: i18n.t('button.save.label'),
                        icon: 'confirm',
                        primary: true,
                        disabled: () => !(fields || []).every((field) => field.validateValue(newRecord)),
                        onClick: onNewRecordConfirmed,
                    }),
                ]
            });

        modal.controls = editorHelpers.createEditorGroupsFromFields(
            modal,
            fields,
            { data: () => newRecord },
            { grid: true }
        );

        return modal;

        async function onNewRecordConfirmed() {
            that.getApp().setBusyFlag();
            try {
                const data = await that.getDataManager().createData(newRecord);
                const id = that.resolveCreatedDataId(data);
                modal.hide();
                that.getApp().routeTo(that.getEditorRouteName(), { id });
                return true;
            } catch (error) {
                that.handleError(error);
                return false;
            } finally {
                that.getApp().clearBusyFlag();
            }
        };

        function onNewRecordCanceled() {
            modal.hide();
        };

    };

    // --------------------------------------------------------------------------

    toggleItemSelection(item) {

        const i18n = Services.get('i18n');

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        item.$selected = !item.$selected;
        this.invalidate();

    };

    toggleItemDetails(item) {

        const i18n = Services.get('i18n');

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        item.$details = !item.$details;
        this.invalidate();

    };

    setAllItemsSelection(selected) {

        (this.getData() || []).forEach(function (item) { item.$selected = selected; });
        this.invalidate();

    };

    setAllItemsDetails(details) {

        (this.getData() || []).forEach(function (item) { item.$details = details; });
        this.invalidate();

    };

    // --------------------------------------------------------------------------

    removeItem(item) {

        var data = this.getData(),
            index = data.indexOf(item);

        if (index < 0)
            return;

        data.splice(index, 1);
        this.invalidate();

    };

    openItem(item) {

        const i18n = Services.get('i18n');

        if (!(item && item.id)) {
            this.getLogger().error(i18n.t('err.no_item_selected'));
            return;
        }

        this.getApp().routeTo(this.getEditorRouteName(), { id: item.id });

    };

    deleteItem(item) {

        const i18n = Services.get('i18n');

        var that = this,
            model = this.getDataModel(),
            modal = new ModalDialog(null, {
                title: i18n.t('module.data_browser.title_record_delete'),
                controls: [
                    i18n.t('module.data_browser.msg_record_delete_confirmation', { name: model.getTitle(item) ?? item.name }),
                ],
                buttons: [
                    new Button(null, {
                        label: i18n.t('button.yes.label'),
                        icon: 'confirm',
                        onClick: deleteConfirmed
                    }),
                    new Button(null, {
                        label: i18n.t('button.no.label'),
                        icon: 'cancel',
                        primary: true,
                        onClick: deleteCanceled
                    })
                ]
            });

        modal.show(this.getApp());

        async function deleteConfirmed() {

            modal.hide();

            that.getApp().setBusyFlag();
            try {
                await that.getDataManager().deleteData(item);
                that.getLogger().success(i18n.t('app.record_deleted'));
                await that.load();
            } catch (error) {
                that.getLogger().error(i18n.t('app.record_delete_failed_details', { details: error }));
            } finally {
                that.getApp().clearBusyFlag();
            }

        };

        function deleteCanceled(e) {
            modal.hide();
        };

    };

    editItem(item) {

        this.checkActiveItemChangesAndExecute(() => {

            this.activeItem = item;
            this.activeItemBackup = Object.assign({}, item);
            this.openEditDialog(item);

        });

    };

    openEditDialog(item) {

        const i18n = Services.get('i18n');
        const model = this.getDataModel();
        const editor = this.createActiveItemEditor(item);
        const modal = new ModalDialog(null, {
            title: model.getTitle(item),
            css: '.medium',
            controls: [editor],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.text',
                    onClick: () => this.cancelActiveItemChanges(),
                }),
                new Button(null, {
                    label: i18n.t('button.save.label'),
                    icon: 'confirm',
                    primary: true,
                    disabled: () => !item?.$changed || !editor.validateData(),
                    onClick: () => this.commitActiveItemChanges(),
                }),
            ]
        });

        this.setViewMode(DataBrowser.MODE_UPDATE, false);
        this.activeItemEditor = editor;
        this.editDialog = modal;
        modal.show(this);
        this.invalidate();

    };

    checkActiveItemChangesAndExecute(execute) {

        const i18n = Services.get('i18n');

        var that = this,
            activeItem = this.getActiveItem(),
            dataModel = this.getDataModel(),
            modal;

        if (!activeItem || !activeItem.$changed) {
            execute.call(this);
            return;
        }

        modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.title_unsaved_changes'),
            controls: [
                i18n.t('module.data_browser.msg_drop_unsaved_changes_confirmation', { name: dataModel.getTitle(activeItem) }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.yes.label'),
                    icon: 'confirm',
                    onClick: onDropUnsavedChangesConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.no.label'),
                    icon: 'cancel',
                    primary: true,
                    onClick: onDropUnsavedChangesRejected
                })
            ]
        });

        modal.show(this);

        function onDropUnsavedChangesConfirmed(e) {

            e.preventDefault();
            modal.hide();
            that.cancelActiveItemChanges();
            execute.call(that);

        };

        function onDropUnsavedChangesRejected(e) {

            e.preventDefault();
            modal.hide();

        };

    };

    changeActiveItemProp(name, value) {

        dto.changeObjectProp(this.activeItem, name, value, this.activeItemValidator);
        this.invalidate();

    };

    async commitActiveItemChanges() {

        const i18n = Services.get('i18n');

        var that = this,
            manager = this.getDataManager(),
            data = dto.getObjectData(this.activeItem);

        this.getApp().setBusyFlag();
        try {
            const updatedData = await manager.updateData(data);
            const updatedId = updatedData?.id ?? data?.id ?? this.activeItem?.id ?? null;
            const refreshedData = updatedId ? await manager.readData(updatedId) : null;
            that.getLogger().success(i18n.t('app.record_updated'));
            if (refreshedData)
                Object.assign(that.activeItem, refreshedData);
            that.activeItem.$changed = false;
            that.activeItem.$details = true;
            that.setViewMode(DataBrowser.MODE_READ);
            if (that.editDialog)
                that.editDialog.hide();
            that.activeItem = null;
            that.activeItemBackup = null;
            that.activeItemEditor = null;
            that.editDialog = null;
            that.invalidate();
        } catch (error) {
            that.getLogger().error(i18n.t('app.record_update_failed_details', { details: error }));
            that.invalidate();
        } finally {
            that.getApp().clearBusyFlag();
        }

    };

    cancelActiveItemChanges() {

        Object.assign(this.activeItem, this.activeItemBackup);
        this.activeItem.$changed = false;
        this.activeItem.$details = true;
        this.setViewMode(DataBrowser.MODE_READ);
        if (this.editDialog)
            this.editDialog.hide();
        this.activeItem = null;
        this.activeItemBackup = null;
        this.activeItemEditor = null;
        this.editDialog = null;
        this.invalidate();

    };

    deleteSelectedItems() {

        const i18n = Services.get('i18n');

        var that = this,
            dataModel = this.getDataModel(),
            selectedItems = this.getSelectedItems(),
            promises,
            modal;

        if (!selectedItems || !selectedItems.length)
            return;

        modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.records_delete_title'),
            controls: [
                i18n.t('module.data_browser.records_delete_confirmation',
                    {
                        names: selectedItems.reduce(function (result, item) {
                            return (result ? result + ', ' : '') + dataModel.getTitle(item);
                        }, null)
                    }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.yes.label'),
                    icon: 'confirm',
                    onClick: deleteItemConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.no.label'),
                    icon: 'cancel',
                    primary: true,
                    onClick: deleteItemCanceled
                })
            ]
        });

        modal.show(this.getApp());

        async function deleteItemConfirmed() {

            modal.hide();

            that.getApp().setBusyFlag();
            try {
                promises = selectedItems.map(async function (item) {
                    try {
                        await that.getDataManager().deleteData(item);
                        that.getLogger().success(i18n.t('app.record_deleted'));
                        that.removeItem(item);
                    } catch (error) {
                        that.getLogger().error(i18n.t('app.record_delete_failed_details', { details: error }));
                    }
                });

                await Promise.all(promises);
                await that.load();
            } catch (error) {
                that.invalidate();
            } finally {
                that.getApp().clearBusyFlag();
            }

        };

        function deleteItemCanceled(e) {
            modal.hide();
        };

    };

    // --------------------------------------------------------------------------

    async _prepare(params) {

        await super._prepare(params);
        this.ensureColumnDefs(true);
        this.ensureEditorDefs(true);

        await Promise.all([
            this.prepareDataQuery(),
            this.prepareColumns()
        ]);

        this.restoreModuleState();

        return this.readData();

    };

    async load(options, activeRoute) {

        await this.readData(options, activeRoute);
        this.invalidate();
        return this.getDataset();

    };

    async readData() {

        var queryString = this.getDataQueryString();

        try {

            this.setDataset(await this.getDataManager().queryData(queryString));

        } catch (error) {

            this.setDataset({});
            throw error;

        }

    };

    // --------------------------------------------------------------------------

    changeDataQueryOrder(columnDef, additive) {

        var query = this.getQueryManager();
        query.updateOrder(columnDef.name, additive);
        this.load();

    };

    changeDataQueryOffset(value) {

        this.getQueryManager().changeQueryOffset(value);
        this.load();

    };

    changeDataQueryLimit(value) {

        this.getQueryManager().changeQueryLimit(value);
        this.load();

    };

    // --------------------------------------------------------------------------------------------

    getExportFormats() {

        return [];

    };

    getExportSelectedFormats() {

        return this.getExportFormats();

    };

    isNewRecordButtonAvailable() {

        return this.getNewRecordFields().length > 0;

    };

    renderPrimaryButtons() {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');

        return [
            ct.iconButton('refresh', '.circle.transparent',
                {
                    onclick: () => { this.load(); }
                },
                {
                    group: 'secondary',
                    enabled: this.isReloadButtonEnabled()
                }
            )
        ];


    };

    renderSecondaryButtons() {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');

        if (!this.isNewRecordButtonAvailable())
            return [];

        return [
            ct.button(i18n.t('button.add.label'), 'add',
                { disabled: !this.isCreateButtonEnabled(), onclick: () => { this.create(); } },
                { group: 'primary' }
            )
        ];

    }

    renderExportButton(formatIds, ids = null, bottom = false) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');
        const el = ct.el;

        const formats = formatIds || [];
        const selectedMode = !!ids;

        if (!formats.length)
            return null;

        return el('.dropdown.dropdown-right' + (bottom ? '.dropdown-up' : ''), {}, [
            el('button.btn.dropdown-toggle', { title: i18n.t(selectedMode ? 'button.export_selected_hint' : 'button.export_all_hint') }, [
                ct.icon('download'),
                ' ',
                i18n.t(ids ? 'button.export_selected' : 'button.export_all'),
                ' ',
                ct.icon('chevron-down')
            ]),
            el('ul.menu', {}, [
                (formats.includes('csv')
                    ? el('li.menu-item', {}, [
                        el('a', { href: this.getExportUrl('csv', ids), target: '_blank', title: i18n.t(selectedMode ? 'ui.label.export_selected_csv_hint' : 'ui.label.export_csv_hint') }, [
                            ct.icon('file-csv'),
                            ' ',
                            i18n.t('ui.label.export_csv')
                        ]),
                    ])
                    : null
                ),
                (formats.includes('excel-xml')
                    ? el('li.menu-item', {}, [
                        el('a', { href: this.getExportUrl('excel-xml', ids), target: '_blank', title: i18n.t(selectedMode ? 'ui.label.export_selected_xml_excel_hint' : 'ui.label.export_xml_excel_hint') }, [
                            ct.icon('file-excel'),
                            ' ',
                            i18n.t('ui.label.export_xml_excel')
                        ]),
                    ])
                    : null
                ),
                (formats.includes('pdf')
                    ? el('li.menu-item', {}, [
                        el('a', { href: this.getExportUrl('pdf', ids), target: '_blank', title: i18n.t(selectedMode ? 'ui.label.export_selected_pdf_report_hint' : 'ui.label.export_pdf_report_hint') }, [
                            ct.icon('file-pdf'),
                            ' ',
                            i18n.t('ui.label.export_pdf_report')
                        ]),
                    ])
                    : null
                ),
                (formats.includes('pdf-zip')
                    ? el('li.menu-item', {}, [
                        el('a', { href: this.getExportUrl('pdf-zip', ids), target: '_blank', title: i18n.t(selectedMode ? 'ui.label.export_selected_pdf_report_zip_hint' : 'ui.label.export_pdf_report_zip_hint') }, [
                            ct.icon('file-archive'),
                            ' ',
                            i18n.t('ui.label.export_pdf_report_zip')
                        ]),
                    ])
                    : null
                ),
            ].filter(Boolean)),
        ]);

    };

    renderHeaderContent(vm) {

        const ct = Services.get('templates');

        return ct.nav([
            ...this.renderPrimaryButtons(),
            ct.div('.max'),
            ...this.renderSecondaryButtons()
        ]);

    };

    renderFooterContent(vm) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');
        const dataset = this.getDataset() || {};

        return [
            ct.paginatorOffset(dataset.count, dataset.offset, dataset.limit, ({ offset }) => this.changeDataQueryOffset(offset)),
            ct.paginatorLimit(dataset.limit, ({ limit }) => this.changeDataQueryLimit(limit)),
        ];

    }

    renderToolbar(vm, bottom = false) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');

        const dataset = this.getDataset() || {};
        const selectedItems = this.getSelectedItems();
        const selectedIds = selectedItems.reduce((result, item) => {
            if (!(item && item.id))
                return result;
            return result ? `${result}+${item.id}` : item.id.toString();
        }, null);

        const cells = [
            ct.cell('.shrink.mr-2', [
                ct.buttonArray(this.getButtons(), (buttonDef) => buttonDef.group == 'secondary' && buttonDef.enabled)
            ]),
            ct.cell('.shrink.mr-2', [
                ct.paginatorOffset(dataset.count, dataset.offset, dataset.limit, ({ offset }) => this.changeDataQueryOffset(offset))
            ]),
            ct.cell('.shrink.mr-3', [
                ct.paginatorLimit(dataset.limit, ({ limit }) => this.changeDataQueryLimit(limit))
            ]),
            ct.cell('.grow'),
        ];

        if (this.isCreateButtonEnabled() && this.isNewRecordButtonAvailable()) {
            cells.push(
                ct.cell('.shrink.ml-1', [
                    ct.buttonArray(this.getButtons(), (buttonDef) => buttonDef.group == 'primary' && buttonDef.enabled)
                ])
            );
        }

        if (this.canUserReadData()) {
            const exportAllButton = this.renderExportButton(this.getExportFormats(), null, bottom);
            if (exportAllButton) {
                cells.push(
                    ct.cell('.shrink.ml-1', [
                        exportAllButton
                    ])
                );
            }
        }

        if (selectedItems.length) {
            const selectedControls = [];
            const exportSelectedButton = this.canUserReadData()
                ? this.renderExportButton(this.getExportSelectedFormats(), selectedIds, bottom)
                : null;

            if (exportSelectedButton) {
                selectedControls.push(exportSelectedButton);
            }

            if (this.canUserDeleteData()) {
                if (selectedControls.length)
                    selectedControls.push(' ');
                selectedControls.push(
                    ct.button(i18n.t('button.delete_selected'), 'delete', {
                        onclick: (e) => {
                            e.preventDefault();
                            this.deleteSelectedItems();
                        }
                    })
                );
            }

            if (selectedControls.length) {
                cells.push(
                    ct.cell('.shrink.ml-1', selectedControls)
                );
            }
        }

        return ct.hstrip('.align-center', cells);

    };

    _render(vm) {

        const ct = Services.get('templates');
        const extraControls = this.getControls()
            .filter((control) => control !== this.uiDataTable)
            .map((control) => control.render(vm));

        return ct.module([
            ct.header([
                this.renderHeaderContent(vm, false),
            ]),
            ct.section([
                this.uiDataTable.render(vm),
            ]),
            ct.footer([
                ct.nav(this.renderFooterContent(vm, true)),
            ]),
        ].concat(extraControls));

    };

}
